import React, { useState, useEffect, useRef } from 'react';
import './ColumnFilter.css';

interface ColumnConfig {
  key: string;
  label: string;
}

interface ColumnFilterProps {
  columns: ColumnConfig[];
  visibleColumns: string[];
  onColumnVisibilityChange: (visibleColumns: string[]) => void;
  defaultColumns?: string[];
  onReset?: () => void;
}

const ColumnFilter: React.FC<ColumnFilterProps> = ({ 
  columns, 
  visibleColumns, 
  onColumnVisibilityChange,
  defaultColumns = [],
  onReset
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleColumnToggle = (columnKey: string) => {
    const newVisibleColumns = visibleColumns.includes(columnKey)
      ? visibleColumns.filter(col => col !== columnKey)
      : [...visibleColumns, columnKey];
    
    onColumnVisibilityChange(newVisibleColumns);
  };

  const handleSelectAll = () => {
    onColumnVisibilityChange(columns.map(col => col.key));
  };

  const handleDeselectAll = () => {
    onColumnVisibilityChange([]);
  };

  const handleResetToDefault = () => {
    if (onReset) {
      onReset();
    } else if (defaultColumns.length > 0) {
      onColumnVisibilityChange(defaultColumns);
    }
  };

  const visibleCount = visibleColumns.length;
  const totalCount = columns.length;
  const hasHiddenColumns = visibleCount < totalCount;

  return (
    <div className="column-filter" ref={dropdownRef}>
      <button 
        className="column-filter-toggle"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>Столбцы</span>
        <span className={`column-count ${hasHiddenColumns ? 'hidden-columns' : ''}`}>
          ({visibleCount}/{totalCount})
        </span>
        <span className={`arrow ${isOpen ? 'up' : 'down'}`}>▼</span>
      </button>

      {isOpen && (
        <div className="column-filter-dropdown">
          <div className="column-filter-header">
            <h4>Видимость столбцов</h4>
            {hasHiddenColumns && (
              <div className="hidden-columns-info">
                Скрыто: {totalCount - visibleCount} столбцов
              </div>
            )}
            <div className="column-filter-actions">
              <button 
                className="select-all-btn"
                onClick={handleSelectAll}
              >
                Выбрать все
              </button>
              <button 
                className="deselect-all-btn"
                onClick={handleDeselectAll}
              >
                Снять выбор
              </button>
              {defaultColumns.length > 0 && (
                <button 
                  className="reset-default-btn"
                  onClick={handleResetToDefault}
                >
                  По умолчанию
                </button>
              )}
            </div>
          </div>

          <div className="column-list">
            {columns.map(column => (
              <label key={column.key} className="column-item">
                <input
                  type="checkbox"
                  checked={visibleColumns.includes(column.key)}
                  onChange={() => handleColumnToggle(column.key)}
                />
                <span className="column-label">{column.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnFilter; 